/** Minimum Y (px) below viewport top where card content may start. */
const MIN_VIEWPORT_TOP_INSET_PX = 164;

/** At least this fraction of the card height should lie inside the vertical safe band when possible. */
const MIN_CARD_VISIBLE_IN_BAND_FRAC = 0.25;

/**
 * Short cards must leave at least this much space below `bandTop` (or above `bandBottom`)
 * so the card is not visually "stuck" under sticky chrome with only a title strip readable.
 */
const MIN_COMFORT_GAP_FRAC_OF_BAND = 0.12;

/**
 * Scroll the window so the given element sits in the vertical band below measured
 * sticky chrome, with at least 25% of its height visible in that band when possible.
 */
export function scrollCardIntoViewIfNeeded(
  el: HTMLElement,
  direction?: 'up' | 'down' | 'left' | 'right',
): void {
  const vh = document.documentElement.clientHeight;
  const vw = document.documentElement.clientWidth;
  const sidePad = 8;
  const bottomPad = 16;
  const bandTop = getViewportTopSafeInsetPx(vh);
  const bandBottom = vh - bottomPad;
  const bandH = Math.max(0, bandBottom - bandTop);

  const r = el.getBoundingClientRect();
  let dx = 0;

  const visibleInBand = Math.max(
    0,
    Math.min(r.bottom, bandBottom) - Math.max(r.top, bandTop),
  );
  const fracInBand = r.height > 0 ? visibleInBand / r.height : 1;
  const gapTop = r.top - bandTop;
  const gapBottom = bandBottom - r.bottom;
  const minComfortGap = Math.min(
    100,
    Math.max(56, MIN_COMFORT_GAP_FRAC_OF_BAND * bandH),
  );
  const verticallyClipped =
    r.top < bandTop - 0.5 || r.bottom > bandBottom + 0.5;
  const crampedInBand =
    !verticallyClipped &&
    r.height <= bandH + 1 &&
    (gapTop < minComfortGap || gapBottom < minComfortGap);
  const fracTooLow =
    r.height <= bandH + 1 && fracInBand + 1e-4 < MIN_CARD_VISIBLE_IN_BAND_FRAC;
  const verticalMove =
    direction === 'up' || direction === 'down' ? direction : null;

  const cardTallerThanBand = r.height > bandH + 1;
  let dy = 0;

  const fullyInBandVertically =
    r.top >= bandTop - 0.5 && r.bottom <= bandBottom + 0.5;
  const shortCardOk =
    !cardTallerThanBand &&
    fullyInBandVertically &&
    fracInBand >= MIN_CARD_VISIBLE_IN_BAND_FRAC &&
    !crampedInBand &&
    !(verticalMove && fracTooLow);

  const tallCardPinned = cardTallerThanBand && Math.abs(r.top - bandTop) < 1.5;

  if (!shortCardOk && !tallCardPinned) {
    if (cardTallerThanBand) {
      dy = r.top - bandTop;
    } else {
      const targetTop = Math.min(
        bandBottom - r.height,
        Math.max(bandTop, bandTop + (bandH - r.height) / 2),
      );
      dy = r.top - targetTop;
    }
  }

  dy = adjustDyForMinVisibleInBand(
    r.top,
    r.height,
    bandTop,
    bandBottom,
    dy,
    MIN_CARD_VISIBLE_IN_BAND_FRAC,
  );

  if (r.left < sidePad) {
    dx = r.left - sidePad;
  } else if (r.right > vw - sidePad) {
    dx = r.right - (vw - sidePad);
  }

  if (dx !== 0 || Math.abs(dy) >= 1) {
    window.scrollBy({ left: dx, top: dy, behavior: 'smooth' });
  }
}

/** Clears any scroll-margin-top previously set on the card element. */
export function clearCardScrollMargin(el: HTMLElement): void {
  el.style.scrollMarginTop = '';
}

/** Bottom edge of sticky / fixed chrome that should sit above the card top. */
function getViewportTopSafeInsetPx(vh: number): number {
  const pad = 12;
  let topSafe = pad;
  const headerHost = document.querySelector<HTMLElement>('app-header');
  if (headerHost) {
    const headerRect = headerHost.getBoundingClientRect();
    topSafe = Math.max(topSafe, headerRect.bottom + pad);
  }
  for (const selector of ['app-claim-header', 'app-claim-menu']) {
    const band = document.querySelector(selector);
    if (!(band instanceof HTMLElement)) continue;
    const b = band.getBoundingClientRect();
    const intersectsViewport = b.height > 0 && b.bottom > topSafe && b.top < vh;
    if (intersectsViewport) {
      topSafe = Math.max(topSafe, b.bottom + 8);
    }
  }

  return Math.max(topSafe, MIN_VIEWPORT_TOP_INSET_PX);
}

/** Fraction of card height lying in [bandTop, bandBottom] when card top is `cardTop`. */
function fracVisibleInVerticalBand(
  cardTop: number,
  cardHeight: number,
  bandTop: number,
  bandBottom: number,
): number {
  const bottom = cardTop + cardHeight;
  const inner = Math.min(bottom, bandBottom) - Math.max(cardTop, bandTop);
  return cardHeight > 0 ? Math.max(0, inner) / cardHeight : 1;
}

/**
 * Adjusts dy (positive scrolls document down) until at least `minFrac` of the
 * card lies in the band when the band is tall enough.
 */
function adjustDyForMinVisibleInBand(
  rTop: number,
  cardHeight: number,
  bandTop: number,
  bandBottom: number,
  dy: number,
  minFrac: number,
): number {
  const bandDepth = Math.max(0, bandBottom - bandTop);
  const maxFracPossible =
    cardHeight > 0 ? Math.min(1, bandDepth / cardHeight) : 1;
  const targetFrac = Math.min(minFrac, maxFracPossible);
  let d = dy;
  for (let i = 0; i < 12; i++) {
    const nf = fracVisibleInVerticalBand(
      rTop - d,
      cardHeight,
      bandTop,
      bandBottom,
    );
    if (nf >= targetFrac - 1e-4) return d;
    const newTop = rTop - d;
    const newBottom = newTop + cardHeight;
    const prev = d;
    if (newBottom > bandBottom) {
      d += newBottom - bandBottom + 1;
    } else if (newTop < bandTop) {
      d -= bandTop - newTop + 1;
    } else {
      break;
    }
    if (Math.abs(d - prev) < 0.5) break;
  }
  return d;
}
